import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Text, Tooltip } from 'barte-design-system';
import { Info, X } from '@phosphor-icons/react';
import './SellerTagInput.css';

// ─── Mock sellers ─────────────────────────────────────────────────────────────

const SELLERS_MOCK = [
  'Loja Central LTDA',
  'Mercado Bom Preço',
  'Padaria Pão Quente',
  'Tech Store',
  'FitLife Academia',
  'Ótica Visão Clara',
  'Pet Shop Animal Love',
  'Barbearia Corte Certo',
  'Lavanderia Express',
  'Farmácia Saúde & Vida',
  'Livraria Cultura',
  'Restaurante Sabor Bom',
  'Supermercado Família',
  'Auto Peças Brasil',
  'Consultório Dr. Lima',
  'Norte Distribuidora',
  'Posto Combustível Star',
  'Varejo Express',
  'Grupo Alfa',
  'Beta Comércio',
];

// Sellers que já estão vinculados a outro plano → pill warning
export const SELLERS_VINCULADOS: Record<string, string> = {
  'Loja Central LTDA': 'Plano seller 4821',
};

// ─── Component ────────────────────────────────────────────────────────────────

interface DropdownRect { top: number; left: number; width: number; }

interface SellerTagInputProps {
  label?: string;
  onSellersChange?: (sellers: string[]) => void;
}

export function SellerTagInput({ label = 'Sellers vinculados', onSellersChange }: SellerTagInputProps) {
  const [selected, setSelected] = useState<string[]>([]);

  function updateSelected(next: string[]) {
    setSelected(next);
    onSellersChange?.(next);
  }
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState<DropdownRect | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);

  const suggestions = SELLERS_MOCK.filter(
    (s) => s.toLowerCase().includes(query.toLowerCase()) && !selected.includes(s),
  );

  function updateRect() {
    if (fieldRef.current) {
      const r = fieldRef.current.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 4, left: r.left, width: r.width });
    }
  }

  function addSeller(seller: string) {
    updateSelected([...selected, seller]);
    setQuery('');
    inputRef.current?.focus();
  }

  function removeSeller(seller: string) {
    updateSelected(selected.filter((s) => s !== seller));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && query === '' && selected.length > 0) {
      updateSelected(selected.slice(0, -1));
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (lines.length > 1) {
      e.preventDefault();
      const next = [...selected];
      for (const line of lines) {
        if (!next.includes(line)) next.push(line);
      }
      updateSelected(next);
      setQuery('');
    }
  }

  const showDropdown = open && query.length > 0 && suggestions.length > 0;

  return (
    <div className="sti-wrapper">
      <Text variant="label-sm" className="sti-label">{label}</Text>
      <div
        ref={fieldRef}
        className="sti-field"
        onClick={() => { inputRef.current?.focus(); setOpen(true); updateRect(); }}
      >
        {selected.map((s) => {
          const planoVinculado = SELLERS_VINCULADOS[s];
          const isWarning = !!planoVinculado;
          return (
            <span key={s} className={`sti-pill${isWarning ? ' sti-pill--warning' : ''}`}>
              {isWarning && (
                <Tooltip
                  label={`Seller vinculado atualmente no plano ${planoVinculado}`}
                  placement="top"
                >
                  <span className="sti-pill-icon">
                    <Info size={12} weight="fill" />
                  </span>
                </Tooltip>
              )}
              <span className="sti-pill-label">{s}</span>
              <button
                className="sti-pill-remove"
                onClick={(e) => { e.stopPropagation(); removeSeller(s); }}
                aria-label={`Remover ${s}`}
              >
                <X size={12} weight="bold" />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          className="sti-input"
          placeholder={selected.length === 0 ? 'Buscar seller...' : ''}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); updateRect(); }}
          onFocus={() => { setOpen(true); updateRect(); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
        />
      </div>

      {showDropdown && dropdownRect && createPortal(
        <div
          className="sti-dropdown"
          style={{ top: dropdownRect.top, left: dropdownRect.left, width: dropdownRect.width }}
        >
          {suggestions.map((s) => (
            <button
              key={s}
              className="sti-option"
              onMouseDown={(e) => { e.preventDefault(); addSeller(s); }}
            >
              <Text variant="body-sm">{s}</Text>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
